import { useState, useEffect, useCallback, useRef } from 'react'
import { useWedding } from '../../../context/WeddingContext'
import { supabase } from '../../../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { Crown, Radio, Check, Search, ExternalLink, Users, Download, Share2 } from 'lucide-react'
import QRCode from 'qrcode'

// ── URL helper (Fix 1) ────────────────────────────────────
function getPortalUrl(slug) {
  const base = import.meta.env.VITE_APP_URL || window.location.origin
  return `${base}/w/${slug}`
}

// ── Wedding Day Mode banner (Fix 2) ──────────────────────
function WeddingDayBanner({ todayEvent, wedding }) {
  if (!todayEvent) return null
  const dateStr = todayEvent.date
    ? new Date(todayEvent.date).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})
    : ''
  return (
    <div style={{ borderRadius:'1.25rem', padding:'2rem 1.5rem', animation:'fadeInBanner 0.6s ease-out', overflow:'hidden', position:'relative', minHeight:170, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <style>{`@keyframes fadeInBanner{from{opacity:0}to{opacity:1}}`}</style>
      {/* Photo background — only renders on the wedding day itself */}
      <img src="/brand/band-moody.webp" srcSet="/brand/band-moody-1200.webp 1200w, /brand/band-moody.webp 2000w" sizes="100vw" alt="" aria-hidden="true"
        style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', objectPosition:'center 30%' }}/>
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(135deg,rgba(122,40,25,0.78),rgba(180,90,55,0.55) 60%,rgba(40,15,8,0.72))' }}/>
      <div style={{ position:'relative', zIndex:1 }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', fontWeight:700, color:'rgba(255,252,250,0.85)', letterSpacing:'0.12em', textTransform:'uppercase', margin:'0 0 0.4rem' }}>
          ✦ Today is your day
        </p>
        <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.6rem', color:'#FEFCFA', margin:'0 0 0.25rem', letterSpacing:'-0.02em', textShadow:'0 2px 12px rgba(0,0,0,0.35)' }}>
          {wedding.partner1_name} & {wedding.partner2_name}
        </p>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'rgba(255,252,250,0.9)', margin:0, textShadow:'0 1px 8px rgba(0,0,0,0.35)' }}>
          {todayEvent.type}{dateStr ? ` · ${dateStr}` : ''}{todayEvent.venue ? ` · ${todayEvent.venue}` : ''}
        </p>
      </div>
    </div>
  )
}

// ── Live Arrivals Counter (Fix 3) ─────────────────────────
function LiveArrivalsCounter({ arrivedCount, totalCount, lastUpdated, reconnecting }) {
  const pct  = totalCount > 0 ? Math.round((arrivedCount/totalCount)*100) : 0
  const r    = 36; const circ = 2*Math.PI*r
  const dash = circ - (pct/100)*circ
  const allArrived = arrivedCount === totalCount && totalCount > 0
  const ringColor  = allArrived ? '#3D8B5E' : '#B36B55'

  const [secAgo, setSecAgo] = useState(0)
  useEffect(()=>{
    const t = setInterval(()=>{ setSecAgo(Math.round((Date.now()-lastUpdated)/1000)) }, 1000)
    return ()=>clearInterval(t)
  },[lastUpdated])

  return (
    <div className="card" style={{ padding:'1.25rem 1.5rem' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'0.875rem' }}>
        {/* Ring */}
        <div style={{ position:'relative', width:80, height:80, flexShrink:0 }}>
          <svg width="80" height="80" style={{ transform:'rotate(-90deg)' }}>
            <circle cx="40" cy="40" r={r} stroke="#F0EBE3" strokeWidth="5" fill="none"/>
            <circle cx="40" cy="40" r={r} stroke={ringColor} strokeWidth="5" fill="none"
              strokeDasharray={circ} strokeDashoffset={dash} strokeLinecap="round"
              style={{ transition:'stroke-dashoffset 0.5s ease, stroke 0.5s ease' }}/>
          </svg>
          <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
            <span style={{ fontFamily:'Gibs, serif', fontSize:'1.4rem', color:ringColor, lineHeight:1 }}>{arrivedCount}</span>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.5rem', color:'#9B9B9B', textTransform:'uppercase', letterSpacing:'0.05em' }}>arrived</span>
          </div>
        </div>
        {/* Info */}
        <div style={{ flex:1 }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#1A1A1A', margin:'0 0 0.2rem' }}>{arrivedCount} of {totalCount} guests</p>
          <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.625rem' }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:'#22C55E', animation:'livePulse 1.5s ease-in-out infinite', boxShadow:'0 0 6px rgba(34,197,94,0.5)' }}/>
            <style>{`@keyframes livePulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.3);opacity:0.7}}`}</style>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', fontWeight:700, color:'#22C55E', textTransform:'uppercase', letterSpacing:'0.1em' }}>Live</span>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', color:'#BCBCBC' }}>· Updated {secAgo}s ago</span>
          </div>
          {/* Progress bar */}
          <div style={{ height:6, background:'#F0EBE3', borderRadius:100, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${pct}%`, background:ringColor, borderRadius:100, transition:'width 0.5s ease' }}/>
          </div>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#9B9B9B', margin:'0.2rem 0 0', textAlign:'right' }}>{pct}% arrived</p>
        </div>
      </div>
      {/* Milestones */}
      {allArrived && (
        <div style={{ marginTop:'0.75rem', background:'rgba(61,139,94,0.08)', border:'1px solid rgba(61,139,94,0.2)', borderRadius:'0.625rem', padding:'0.5rem 0.875rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#3D8B5E', fontWeight:600, margin:0 }}>✓ All guests have arrived</p>
        </div>
      )}
      {!allArrived && pct>=90 && (
        <div style={{ marginTop:'0.75rem', background:'rgba(155,175,155,0.1)', borderRadius:'0.625rem', padding:'0.5rem 0.875rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#5A7A5A', margin:0 }}>Almost everyone is here — {totalCount-arrivedCount} guests still to arrive</p>
        </div>
      )}
      {!allArrived && pct>=50 && pct<90 && (
        <div style={{ marginTop:'0.75rem', background:'rgba(179,107,85,0.06)', borderRadius:'0.625rem', padding:'0.5rem 0.875rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#B36B55', margin:0 }}>Half your guests have arrived</p>
        </div>
      )}
      {reconnecting && (
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#D97706', margin:'0.5rem 0 0' }}>⚠ Reconnecting…</p>
      )}
    </div>
  )
}

// ── Stream Link Card (Fix 6) ──────────────────────────────
function StreamLinkCard({ weddingId }) {
  const [link, setLink]     = useState('')
  const [saved, setSaved]   = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(()=>{
    if(!weddingId) return
    supabase.from('weddings').select('stream_link').eq('id',weddingId).maybeSingle()
      .then(({data})=>{ if(data?.stream_link){ setSaved(data.stream_link); setLink(data.stream_link) } })
  },[weddingId])

  async function saveLink() {
    setSaving(true)
    await supabase.from('weddings').update({stream_link:link.trim()||null}).eq('id',weddingId)
    setSaved(link.trim())
    setSaving(false)
  }

  return (
    <div className="card" style={{ padding:'1.125rem 1.25rem' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#1A1A1A', margin:'0 0 0.25rem' }}>📺 Live Stream Link</p>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:'0 0 0.75rem' }}>Share a Zoom, YouTube, or Google Meet link with guests.</p>
      <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
        <input className="input-field" placeholder="https://youtube.com/live/..." value={link} onChange={e=>setLink(e.target.value)} style={{ flex:'1 1 200px', fontSize:'0.8rem' }}/>
        <button onClick={saveLink} disabled={saving} className="btn-primary" style={{ fontSize:'0.78rem', padding:'0.5rem 1rem', flexShrink:0 }}>
          {saving?'Saving…':'Save Link'}
        </button>
        {saved && (
          <a href={saved} target="_blank" rel="noreferrer" title="Open your stream link to verify it works"
            style={{ display:'flex', alignItems:'center', gap:'0.3rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700, color:'#B36B55', background:'transparent', border:'1.5px solid rgba(179,107,85,0.4)', borderRadius:100, padding:'0.5rem 0.875rem', textDecoration:'none', flexShrink:0 }}>
            <ExternalLink size={12}/> Test Link
          </a>
        )}
      </div>
    </div>
  )
}

// ── Guest row (Fix 7) ─────────────────────────────────────
function GuestRow({ guest, arrived, time, onMarkArrived, onUndo }) {
  const [loading, setLoading]   = useState(false)
  const [undoVisible, setUndoVisible] = useState(false)

  async function handleMark() {
    setLoading(true)
    await onMarkArrived(guest)
    setLoading(false)
    setUndoVisible(true)
    setTimeout(()=>setUndoVisible(false), 10000)
  }

  return (
    <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', background:'white', border:`1.5px solid ${arrived?'rgba(61,139,94,0.3)':'#E5E0D8'}`, borderLeft: arrived?'3px solid rgba(61,139,94,0.5)':undefined, borderRadius:'0.75rem', padding:'0.75rem 1rem', transition:'border 0.3s' }}>
      <div style={{ width:34,height:34,borderRadius:'50%',background:'rgba(196,130,106,0.1)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,position:'relative' }}>
        <span style={{ fontFamily:'Gibs, serif', fontSize:'0.9rem', color:'#C4826A' }}>{guest.name?.[0]?.toUpperCase()}</span>
        {arrived && <div style={{ position:'absolute',inset:0,borderRadius:'50%',border:'2px solid rgba(61,139,94,0.5)',display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(61,139,94,0.06)' }}><Check size={12} color="#3D8B5E"/></div>}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#1A1A1A', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{guest.name}</p>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:arrived?'#3D8B5E':'#BCBCBC', margin:0, fontWeight:arrived?600:400 }}>
            {arrived
              ? `Arrived · ${time ? new Date(time).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}) : '—'}`
              : 'Not yet arrived'}
          </p>
          {arrived && undoVisible && (
            <button onClick={()=>onUndo(guest)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', fontWeight:600, color:'#B36B55', background:'none', border:'none', cursor:'pointer', padding:0 }}>Undo</button>
          )}
        </div>
      </div>
      {!arrived && (
        <button onClick={handleMark} disabled={loading}
          style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'white', background:'#3D8B5E', border:'none', borderRadius:100, padding:'0.45rem 0.875rem', cursor:'pointer', flexShrink:0, minWidth:90, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.3rem' }}>
          {loading ? <span style={{ width:10,height:10,border:'2px solid rgba(255,255,255,0.5)',borderTopColor:'white',borderRadius:'50%',display:'inline-block',animation:'spin 0.7s linear infinite' }}/> : <><Check size={12}/> Mark Arrived</>}
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </button>
      )}
      {arrived && (
        <div style={{ display:'flex', alignItems:'center', gap:'0.3rem', background:'rgba(61,139,94,0.1)', borderRadius:100, padding:'0.35rem 0.75rem', flexShrink:0 }}>
          <Check size={12} color="#3D8B5E"/>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', fontWeight:700, color:'#3D8B5E' }}>Arrived</span>
        </div>
      )}
    </div>
  )
}

// ── QR Scan (Fix 5) ───────────────────────────────────────
function QRScanView({ onFound, onClose }) {
  const videoRef   = useRef()
  const canvasRef  = useRef()
  const [err, setErr] = useState('')
  useEffect(()=>{
    let stream; let raf; let active = true
    async function start() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video:{ facingMode:'environment' } })
        videoRef.current.srcObject = stream
        videoRef.current.play()
        scan()
      } catch(e) { setErr('Camera not available. Use Manual Search to check in guests.') }
    }
    async function scan() {
      if (!active) return
      if (!('BarcodeDetector' in window)) { setErr('QR scanning not supported on this browser. Use Manual Search.'); return }
      const detector = new window.BarcodeDetector({ formats:['qr_code'] })
      const loop = async () => {
        if (!videoRef.current || !active) return
        try {
          const codes = await detector.detect(videoRef.current)
          if (codes.length > 0) { onFound(codes[0].rawValue); return }
        } catch(e) {}
        raf = requestAnimationFrame(loop)
      }
      loop()
    }
    start()
    return ()=>{ active=false; cancelAnimationFrame(raf); stream?.getTracks().forEach(t=>t.stop()) }
  },[])

  if (err) return (
    <div style={{ background:'rgba(244,179,0,0.06)', border:'1px solid rgba(244,179,0,0.2)', borderRadius:'0.875rem', padding:'1.25rem', textAlign:'center' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', margin:'0 0 0.875rem', lineHeight:1.6 }}>{err}</p>
      <button onClick={onClose} className="btn-secondary" style={{ fontSize:'0.78rem' }}>Back to Manual Search</button>
    </div>
  )

  return (
    <div style={{ position:'relative', borderRadius:'1rem', overflow:'hidden', border:'2px solid #B36B55' }}>
      <video ref={videoRef} style={{ width:'100%', maxHeight:280, objectFit:'cover', display:'block' }} playsInline muted/>
      <canvas ref={canvasRef} style={{ display:'none' }}/>
      <div style={{ position:'absolute', inset:0, border:'4px solid rgba(179,107,85,0.5)', borderRadius:'1rem', animation:'scanBorder 1.5s ease-in-out infinite', pointerEvents:'none' }}/>
      <style>{`@keyframes scanBorder{0%,100%{opacity:0.5}50%{opacity:1}}`}</style>
      <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'linear-gradient(to top,rgba(0,0,0,0.7),transparent)', padding:'1rem', textAlign:'center' }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'white', margin:'0 0 0.5rem' }}>Point camera at guest QR code</p>
        <button onClick={onClose} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, color:'rgba(255,255,255,0.7)', background:'rgba(0,0,0,0.3)', border:'none', borderRadius:100, padding:'0.35rem 1rem', cursor:'pointer' }}>Cancel</button>
      </div>
    </div>
  )
}

// ── Portal QR Card ────────────────────────────────────────
function PortalQRCard({ slug }) {
  const canvasRef = useRef()
  const [copied, setCopied] = useState(false)
  const url = getPortalUrl(slug)

  useEffect(() => {
    if (!canvasRef.current) return
    QRCode.toCanvas(canvasRef.current, url, {
      width: 210,
      margin: 2,
      color: { dark: '#1A1A1A', light: '#FFFFFF' }
    })
  }, [url])

  async function handleDownload() {
    const dataUrl = await QRCode.toDataURL(url, {
      width: 1200, margin: 3,
      color: { dark: '#1A1A1A', light: '#FFFFFF' }
    })
    const a = document.createElement('a')
    a.href = dataUrl
    a.download = `${slug || 'wedding'}-guest-portal-qr.png`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
  }

  async function handleShare() {
    if (navigator.share) {
      try { await navigator.share({ title: 'Our Wedding Portal', url }); return }
      catch (e) { if (e.name === 'AbortError') return }
    }
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  return (
    <div className="card" style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.05rem', color: '#1A1A1A', margin: '0 0 0.4rem' }}>Guest Portal QR Code</p>
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.75rem', color: '#6B6B6B', margin: 0, lineHeight: 1.7, maxWidth: 340 }}>
            Print and place at the <strong style={{ color: '#1A1A1A' }}>venue entrance</strong>, on each <strong style={{ color: '#1A1A1A' }}>reception table</strong>, inside <strong style={{ color: '#1A1A1A' }}>welcome bags</strong>, or on your <strong style={{ color: '#1A1A1A' }}>order of service</strong> — guests scan to access your love story, live stream, registry, and gallery in one place.
          </p>
        </div>

        <canvas ref={canvasRef} style={{ borderRadius: '1rem', border: '1.5px solid #E5E0D8', padding: 10, background: 'white' }}/>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button onClick={handleDownload}
            style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.8rem', fontWeight: 700, color: 'white', background: '#B36B55', border: 'none', borderRadius: 100, padding: '0.6rem 1.375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Download size={13}/> Download QR
          </button>
          <button onClick={handleShare}
            style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.8rem', fontWeight: 700, color: copied ? '#3D8B5E' : '#B36B55', background: 'transparent', border: `1.5px solid ${copied ? 'rgba(61,139,94,0.4)' : 'rgba(179,107,85,0.4)'}`, borderRadius: 100, padding: '0.6rem 1.375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'all 0.2s' }}>
            <Share2 size={13}/> {copied ? '✓ Link Copied!' : 'Share Link'}
          </button>
        </div>

        <a href={url} target="_blank" rel="noreferrer"
          style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', color: '#BCBCBC', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
          onMouseEnter={e => e.currentTarget.style.color = '#9B9B9B'}
          onMouseLeave={e => e.currentTarget.style.color = '#BCBCBC'}>
          <ExternalLink size={11}/> Preview guest portal
        </a>
      </div>
    </div>
  )
}

// ── Check-In Tab ──────────────────────────────────────────
function CheckInTab({ wedding, events }) {
  const [selectedEvent, setSelectedEvent] = useState(events?.[0]||null)
  const [guests, setGuests]               = useState([])
  const [search, setSearch]               = useState('')
  const [scanMode, setScanMode]           = useState(false)
  const [scannedGuest, setScannedGuest]   = useState(null)
  const [reconnecting, setReconnecting]   = useState(false)
  const [lastUpdated, setLastUpdated]     = useState(Date.now())

  useEffect(()=>{ if(events?.length) setSelectedEvent(events[0]) },[events])

  const fetchGuests = useCallback(async()=>{
    if(!selectedEvent?.id) return
    const { data } = await supabase.from('guests').select('*').order('name')
    const eventGuests = (data||[]).filter(g=>{
      const ids=g.invited_event_ids; return !ids||ids.length===0||ids.includes(selectedEvent.id)
    })
    setGuests(eventGuests)
    setLastUpdated(Date.now())
  },[selectedEvent?.id])

  useEffect(()=>{ fetchGuests() },[fetchGuests])

  // Real-time subscription (Fix 4)
  useEffect(()=>{
    if(!wedding?.id) return
    const channel = supabase.channel('guests-checkin')
      .on('postgres_changes',{event:'UPDATE',schema:'public',table:'guests'},()=>{ fetchGuests() })
      .subscribe(status=>{ setReconnecting(status==='CLOSED'||status==='CHANNEL_ERROR') })
    return ()=>{ supabase.removeChannel(channel) }
  },[wedding?.id, fetchGuests])

  // Broadcast arrival sync with the attendant check-in link (RLS-free, instant)
  const arrivalsRef = useRef(null)
  useEffect(()=>{
    if(!wedding?.id) return
    const ch = supabase.channel(`arrivals-${wedding.id}`)
      .on('broadcast',{event:'arrival'},({payload})=>{
        setGuests(prev=>prev.map(g=>g.id===payload.guestId?{...g,...payload.fields}:g))
      })
      .subscribe()
    arrivalsRef.current = ch
    return ()=>{ supabase.removeChannel(ch); arrivalsRef.current = null }
  },[wedding?.id])

  // Per-event arrival — same model as the coordinator's toggle_guest_arrival
  // RPC: maintain arrived_event_ids, with the `arrived` boolean + arrived_at
  // derived from it, so the door and this dashboard stay one source of truth.
  function applyArrival(guest, fields) {
    supabase.from('guests').update(fields).eq('id', guest.id).then(()=>{})
    setGuests(prev=>prev.map(g=>g.id===guest.id?{...g,...fields}:g))
    arrivalsRef.current?.send({ type:'broadcast', event:'arrival', payload:{ guestId:guest.id, fields } })
    setLastUpdated(Date.now())
  }

  async function markArrived(guest) {
    const eventId = selectedEvent?.id
    const at = guest.arrived_at || new Date().toISOString()
    if (!eventId) { applyArrival(guest, { arrived:true, arrived_at:at }); return }
    const cur = guest.arrived_event_ids || []
    const newIds = cur.includes(eventId) ? cur : [...cur, eventId]
    applyArrival(guest, { arrived_event_ids:newIds, arrived:newIds.length>0, arrived_at:at })
  }

  async function undoArrival(guest) {
    const eventId = selectedEvent?.id
    if (!eventId) { applyArrival(guest, { arrived:false, arrived_at:null }); return }
    const newIds = (guest.arrived_event_ids || []).filter(id=>id!==eventId)
    const isArr = newIds.length>0
    applyArrival(guest, { arrived_event_ids:newIds, arrived:isArr, arrived_at:isArr ? (guest.arrived_at||null) : null })
  }

  function handleQRScan(rawValue) {
    // rawValue is a URL like /w/slug/checkin/token
    const token = rawValue.split('/').pop()
    const found = guests.find(g=>g.invite_token===token)
    if (found) setScannedGuest(found)
    setScanMode(false)
  }

  const isArrived = (g) => selectedEvent ? (g.arrived_event_ids||[]).includes(selectedEvent.id) : (g.arrived||g.checked_in)
  const arrived = guests.filter(isArrived)
  const filtered = guests.filter(g=>!search||g.name?.toLowerCase().includes(search.toLowerCase()))
  const isLocalhost = window.location.hostname==='localhost'

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      {/* Portal QR code */}
      <PortalQRCard slug={wedding.slug}/>

      {/* Live arrivals counter (Fix 3) */}
      <LiveArrivalsCounter arrivedCount={arrived.length} totalCount={guests.length} lastUpdated={lastUpdated} reconnecting={reconnecting}/>

      {/* Event selector */}
      {events?.length > 1 && (
        <div style={{ display:'flex', gap:'0.5rem', overflowX:'auto', scrollbarWidth:'none' }}>
          {events.map(ev=>(
            <button key={ev.id} onClick={()=>setSelectedEvent(ev)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:selectedEvent?.id===ev.id?700:500, color:selectedEvent?.id===ev.id?'#B36B55':'#6B6B6B', background:selectedEvent?.id===ev.id?'rgba(179,107,85,0.08)':'transparent', border:`1.5px solid ${selectedEvent?.id===ev.id?'rgba(179,107,85,0.3)':'#E5E0D8'}`, borderRadius:100, padding:'0.4rem 0.875rem', cursor:'pointer', whiteSpace:'nowrap' }}>{ev.type}</button>
          ))}
        </div>
      )}

      {/* Scanned guest confirmation */}
      {scannedGuest && (
        <div style={{ background:'rgba(61,139,94,0.08)', border:'1.5px solid rgba(61,139,94,0.3)', borderRadius:'0.875rem', padding:'1rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.88rem', color:'#1A1A1A', margin:'0 0 0.25rem' }}>✓ {scannedGuest.name}</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B', margin:'0 0 0.75rem' }}>{selectedEvent?.type}</p>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={()=>{ markArrived(scannedGuest); setScannedGuest(null) }} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700, color:'white', background:'#3D8B5E', border:'none', borderRadius:100, padding:'0.5rem 1rem', cursor:'pointer' }}>Mark Arrived</button>
            <button onClick={()=>setScannedGuest(null)} className="btn-secondary" style={{ fontSize:'0.78rem' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Tip: what arriving guests will present */}
      <div style={{ background:'rgba(196,130,106,0.07)', border:'1px solid rgba(196,130,106,0.18)', borderRadius:'0.75rem', padding:'0.7rem 0.875rem' }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', color:'#8C6B57', margin:0, lineHeight:1.55 }}>
          💡 Guests confirm online and save an <strong>RSVP confirmation card</strong>. At the door, match the name on their card, search below or scan their QR, and tap to check them in.
        </p>
      </div>

      {/* Search / QR toggle (Fix 5) */}
      <div style={{ position:'relative' }}>
        <Search size={13} color="#BCBCBC" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }}/>
        <input className="input-field" placeholder="Search guests…" value={search} onChange={e=>setSearch(e.target.value)} style={{ paddingLeft:'2rem', fontSize:'0.82rem' }}/>
      </div>

      {/* Guest list */}
      <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
        {filtered.length===0
          ? <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#BCBCBC', textAlign:'center', padding:'2rem' }}>No guests found</p>
          : filtered.map(g=>(
            <GuestRow key={g.id} guest={g} arrived={isArrived(g)} time={g.arrived_at||g.checked_in_at} onMarkArrived={markArrived} onUndo={undoArrival}/>
          ))
        }
      </div>

      {/* Coordinator card */}
      {wedding?.checkin_attendant_phone ? (
        <div style={{ background:'rgba(61,139,94,0.06)', border:'1px solid rgba(61,139,94,0.2)', borderRadius:'0.875rem', padding:'1rem 1.125rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#3D8B5E', margin:'0 0 0.25rem' }}>✓ Coordinator assigned</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 0.35rem' }}>
            {wedding.checkin_attendant_name || 'Coordinator'} has check-in access via a secure PIN-protected link.
          </p>
          <button onClick={()=>window.location.href='/dashboard/settings#coordinator'} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, color:'#B36B55', background:'none', border:'none', cursor:'pointer', padding:0 }}>Manage in Settings →</button>
        </div>
      ) : (
        <div style={{ background:'rgba(244,179,0,0.05)', border:'1px solid rgba(244,179,0,0.2)', borderRadius:'0.875rem', padding:'1rem 1.125rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#1A1A1A', margin:'0 0 0.35rem' }}>💡 Delegate check-in to a coordinator</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 0.75rem', lineHeight:1.6 }}>A trusted person can manage guest arrivals on their own phone — no login needed. They get a secure PIN-protected link.</p>
          <button onClick={()=>window.location.href='/dashboard/settings#coordinator'} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700, color:'#B36B55', background:'rgba(179,107,85,0.1)', border:'1px solid rgba(179,107,85,0.2)', borderRadius:100, padding:'0.45rem 1rem', cursor:'pointer' }}>+ Add Check-In Coordinator</button>
        </div>
      )}
    </div>
  )
}

// ── Live Updates Tab ──────────────────────────────────────
function LiveUpdatesTab({ weddingId }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      <StreamLinkCard weddingId={weddingId}/>
    </div>
  )
}

// ── Main ─────────────────────────────────────────────────
export default function WeddingDayPage() {
  const { wedding, events , isPremium } = useWedding()
  const navigate            = useNavigate()
  const [tab, setTab]       = useState('updates')

  // Wedding Day Mode — check if today matches any event date (Fix 2)
  const today = new Date().toDateString()
  const todayEvent = events?.find(ev => ev.date && new Date(ev.date).toDateString()===today)

  if (!wedding) return null

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>
      {/* Wedding Day Mode banner */}
      <WeddingDayBanner todayEvent={todayEvent} wedding={wedding}/>

      <div>
        <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(1.4rem,3vw,1.8rem)', color:'#1A1A1A', margin:'0 0 0.25rem', letterSpacing:'-0.03em' }}>Wedding Day</h1>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', margin:0 }}>Manage check-ins and send live updates to guests in real time.</p>
      </div>

      {!isPremium && (
        <div style={{ background:'linear-gradient(135deg,rgba(196,130,106,0.08),rgba(155,175,155,0.08))', border:'1.5px solid rgba(196,130,106,0.25)', borderRadius:'1.25rem', padding:'1.75rem', textAlign:'center' }}>
          <Crown size={28} color="#C9A84C" style={{ marginBottom:'0.875rem' }}/>
          <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', color:'#1A1A1A', margin:'0 0 0.5rem' }}>Wedding Day features are Premium</h3>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#6B6B6B', margin:'0 0 1.25rem', lineHeight:1.65 }}>QR guest check-in and live updates keep your day running smoothly — both require Premium.</p>
          <button className="btn-primary" onClick={()=>navigate('/pricing')} style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem' }}><Crown size={14}/> Upgrade to Unlock</button>
        </div>
      )}

      {isPremium && (
        <>
          <div style={{ display:'flex', gap:'0.25rem', borderBottom:'1px solid #E5E0D8' }}>
            {[{id:'updates',icon:Radio,label:'Live Updates'},{id:'checkin',icon:Users,label:'Guest Check-In'}].map(t=>(
              <button key={t.id} onClick={()=>setTab(t.id)} style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.625rem 1.125rem', background:'none', border:'none', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', fontWeight:tab===t.id?700:500, color:tab===t.id?'#C4826A':'#6B6B6B', borderBottom:tab===t.id?'2px solid #C4826A':'2px solid transparent', marginBottom:'-1px', transition:'all 0.15s' }}>
                <t.icon size={14}/>{t.label}
                {todayEvent && t.id==='checkin' && <span style={{ width:7,height:7,borderRadius:'50%',background:'#B36B55',display:'inline-block',animation:'livePulse 1.5s ease-in-out infinite' }}/>}
              </button>
            ))}
          </div>
          {tab==='updates' && <LiveUpdatesTab weddingId={wedding.id} weddingSlug={wedding.slug}/>}
          {tab==='checkin' && <CheckInTab wedding={wedding} events={events}/>}
        </>
      )}
    </div>
  )
}
